import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField } from "@mui/material";
import { authStore } from "@/stores/Stores";

const SecurityCode: React.FC<{
  open: boolean;
  onCancel?: () => void;
  onConfirm: () => void;
}> = ({ open, onCancel = () => {}, onConfirm = () => {} }) => {
  const { t } = useTranslation();
  const inputRefs = Array.from({ length: 6 }, () => useRef<HTMLInputElement>(null)); // Create refs for each input
  const [code, setCode] = useState<string[]>(Array(6).fill(""));

  useEffect(() => {
    if (!open) {
      setCode(Array(6).fill(""));
    }
  }, [open]);

  // Handler to move focus
  const handleChange = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1); // Only allow single digit
    const newCode = [...code];

    newCode[idx] = value;
    setCode(newCode);

    authStore.securityCode[idx] = value;

    if (value && idx < inputRefs.length - 1) {
      inputRefs[idx + 1].current?.focus();
    }
  };

  const validateCode = () => !code.some(v => v === "");

  const handleKeyDown = (idx: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    }
  };

  return (
    <Dialog maxWidth="xs" open={open} onClose={onCancel}>
      <DialogTitle>{t("Security Code")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ marginBottom: "1rem" }}>
          {t("Please enter the 2FA code from your authentication app to complete the login process")}.
        </DialogContentText>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ padding: "1rem 0" }}>
          {inputRefs.map((ref, idx) => (
            <TextField
              key={idx}
              inputRef={ref}
              autoFocus={idx === 0}
              variant="outlined"
              size="medium"
              type="number"
              slotProps={{ htmlInput: { maxLength: 1, style: { textAlign: "center" } } }}
              sx={{ width: "2.5rem" }}
              value={code[idx]}
              onChange={handleChange(idx)}
              onKeyDown={handleKeyDown(idx)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={!validateCode()} onClick={onConfirm} variant="contained" autoFocus>
          {t("Log in")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecurityCode;
